import React, { useState, useRef, useEffect, useId } from 'react'
import { cn } from '@strata-monorepo/shared-ui/utils/cn'

export interface CustomSelectOption {
  key: string
  value: string
  label: string
  disabled?: boolean
}

export interface CustomSelectProps {
  options: CustomSelectOption[]
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  triggerClassName?: string
  triggerTextAlignment?: 'left' | 'center' | 'right'
  dataTestId?: string
  disabled?: boolean
  renderValue?: (selectedOption: CustomSelectOption | undefined) => React.ReactNode
  dropdownIcon?: React.ReactNode | ((isOpen: boolean) => React.ReactNode)
  slim?: boolean
  /** Applied to the trigger button. `<button>` is labelable, so a `<label htmlFor>` pairs with it. */
  id?: string
  ariaLabel?: string
  /** ID of an element labelling the trigger. Must be a real element ID, not a test id. */
  ariaLabelledBy?: string
  /**
   * Additional Tailwind classes applied to each dropdown option button.
   * The button's `padding`, `backgroundColor`, `color`, `display`, `width`,
   * `border`, `cursor`, and `opacity` are set via inline styles above and take
   * precedence over any class targeting those same properties — this prop is
   * only reliable for properties the inline styles don't touch (e.g. `height`,
   * `min-height`, `font-size`).
   */
  dropdownItemClassName?: string
  appearOnTop?: boolean
}

// ┌─────────────────────────────────────────────────────────────┐
// │                                                             │
// │   Having trouble with Select inside a Modal or Drawer?      │
// │                                                             │
// │   USE THIS COMPONENT.                                       │
// │                                                             │
// │   It works inside portals and nested contexts.              │
// └─────────────────────────────────────────────────────────────┘

/**
 * CustomSelect - A custom dropdown select component built from scratch
 *
 * This component was created because ShadCN/Radix Select components have issues
 * when used inside modals, drawers, or other portal-based containers
 *
 * This implementation renders the dropdown inline (no portal) with fixed/absolute
 * positioning, keeping it within the parent's DOM hierarchy and avoiding these conflicts.
 *
 * This should be the consolidated Select component for the codebase - other select
 * implementations should migrate to use this component.
 */
export function CustomSelect({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  className,
  triggerClassName,
  triggerTextAlignment = 'center',
  dataTestId,
  disabled = false,
  renderValue,
  dropdownIcon,
  slim = false,
  id,
  ariaLabel,
  ariaLabelledBy,
  dropdownItemClassName,
  appearOnTop = false,
}: CustomSelectProps): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false)
  const listboxId = useId()
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
  const buttonRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const selectedOption = options.find((option) => option.value === value)

  // Focus dropdown when opened
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        dropdownRef.current?.focus()
      })
    }
  }, [isOpen])

  // Helper to calculate position from button
  const calculatePosition = () => {
    if (!buttonRef.current) return { top: 0, left: 0, width: 0 }
    const rect = buttonRef.current.getBoundingClientRect()
    // For absolute positioning, use button height since dropdown is inside the relative container
    return {
      top: rect.height,
      left: 0,
      width: rect.width,
    }
  }

  const handleButtonClick = () => {
    if (!disabled) {
      if (!isOpen) {
        // Calculate position before opening
        setDropdownPosition(calculatePosition())
      }
      setIsOpen(!isOpen)
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null

      // If dropdown ref isn't set yet, don't close
      if (!dropdownRef.current) return

      // WHY composedPath: on a document listener, an event originating inside a
      // Shadow DOM is retargeted to the shadow host, so `contains(event.target)`
      // is false even for taps on our own trigger. Without this, tapping the
      // trigger to close reads as an outside tap and closes on touchstart, then
      // the trailing click reopens it — so it never closes.
      const path = typeof event.composedPath === 'function' ? event.composedPath() : []
      const isInside = [buttonRef.current, dropdownRef.current].some(
        (element) =>
          element !== null &&
          (path.includes(element) || (target !== null && element.contains(target))),
      )

      // Only close if click is outside both button and dropdown
      if (!isInside) {
        setIsOpen(false)
      }
    }

    // Use bubble phase (not capture) so option clicks fire first
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [isOpen])

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault()
        setIsOpen(!isOpen)
        break
      case 'Escape':
        event.preventDefault()
        setIsOpen(false)
        buttonRef.current?.focus()
        break
      case 'ArrowDown':
        event.preventDefault()
        if (!isOpen) {
          setIsOpen(true)
        }
        break
      case 'ArrowUp':
        event.preventDefault()
        if (!isOpen) {
          setIsOpen(true)
        }
        break
    }
  }

  const handleOptionSelect = (optionValue: string, optionDisabled?: boolean) => {
    if (optionDisabled) return

    onChange(optionValue)
    setIsOpen(false)
    buttonRef.current?.focus()
  }

  const handleOptionKeyDown = (
    event: React.KeyboardEvent,
    optionValue: string,
    optionDisabled?: boolean,
  ) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault()
        handleOptionSelect(optionValue, optionDisabled)
        break
      case 'Escape':
        event.preventDefault()
        setIsOpen(false)
        buttonRef.current?.focus()
        break
    }
  }

  const renderDropdown = () => {
    if (!isOpen) return null

    const dropdown = (
      <div
        ref={dropdownRef}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          top: appearOnTop ? 'unset' : `${dropdownPosition.top}px`,
          bottom: appearOnTop ? `${dropdownPosition.top}px` : 'unset',
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`,
          zIndex: 999999,
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '4px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          maxHeight: '240px',
          overflow: 'auto',
          ...(!appearOnTop && {
            marginTop: '4px',
          }),
          ...(appearOnTop && {
            marginBottom: '4px',
          }),
        }}
        id={listboxId}
        role='listbox'
        aria-label={ariaLabel}
        aria-labelledby={ariaLabel ? undefined : (ariaLabelledBy ?? id)}
      >
        {options.map((option) => (
          <button
            key={option.key}
            type='button'
            role='option'
            aria-selected={option.value === value}
            aria-disabled={option.disabled}
            disabled={option.disabled}
            onClick={(e) => {
              e.stopPropagation()
              handleOptionSelect(option.value, option.disabled)
            }}
            onKeyDown={(e) => handleOptionKeyDown(e, option.value, option.disabled)}
            className={dropdownItemClassName}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '8px 12px',
              border: 'none',
              cursor: option.disabled ? 'not-allowed' : 'pointer',
              // eslint-disable-next-line local/no-hardcoded-hex -- Tailwind default palette color; no Strata system token equivalent
              backgroundColor: option.value === value ? '#eff6ff' : 'white',
              // eslint-disable-next-line local/no-hardcoded-hex -- Tailwind default palette color; no Strata system token equivalent
              color: option.value === value ? '#2563eb' : 'black',
              opacity: option.disabled ? 0.5 : 1,
            }}
            onMouseEnter={(e) => {
              if (!option.disabled) {
                e.currentTarget.style.backgroundColor =
                  // eslint-disable-next-line local/no-hardcoded-hex -- Tailwind default palette colors; no Strata system token equivalent
                  option.value === value ? '#eff6ff' : '#f3f4f6'
              }
            }}
            onMouseLeave={(e) => {
              // eslint-disable-next-line local/no-hardcoded-hex -- Tailwind default palette color; no Strata system token equivalent
              e.currentTarget.style.backgroundColor = option.value === value ? '#eff6ff' : 'white'
            }}
          >
            {option.label}
          </button>
        ))}
      </div>
    )

    return dropdown
  }

  return (
    <div className={cn('strata:relative', className)}>
      <button
        ref={buttonRef}
        type='button'
        onClick={handleButtonClick}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        id={id}
        // The ARIA select-only combobox pattern. Also what a native <select>
        // exposes, so the two are interchangeable to assistive tech and to
        // role-based queries.
        role='combobox'
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-haspopup='listbox'
        aria-label={ariaLabel}
        // Only reference a real element ID. This previously pointed at
        // `dataTestId`, which is rendered as `data-testid` — never an `id` —
        // so the trigger resolved to no accessible name at all.
        aria-labelledby={ariaLabel ? undefined : ariaLabelledBy}
        data-testid={dataTestId}
        className={cn(
          'strata:border strata:border-gray-200 strata:bg-gray-50',
          'strata:text-body-base strata:h-8',
          'strata:w-full strata:@sm:min-w-53',
          'strata:px-3 strata:py-2 strata:rounded',
          'strata:flex strata:items-center strata:justify-between',
          'strata:text-left',
          'strata:transition-colors',
          'focus:strata:outline-none focus:strata:ring-2 focus:strata:ring-blue-500',
          disabled
            ? 'strata:opacity-50 strata:cursor-not-allowed'
            : 'strata:cursor-pointer hover:strata:bg-gray-100',
          slim && 'strata:justify-center',
          triggerClassName,
        )}
      >
        <span
          className={cn('strata:truncate', !slim && 'strata:flex-1', {
            'strata:text-left': triggerTextAlignment === 'left',
            'strata:text-center': triggerTextAlignment === 'center',
            'strata:text-right': triggerTextAlignment === 'right',
          })}
        >
          {renderValue ? renderValue(selectedOption) : selectedOption?.label || placeholder}
        </span>
        {dropdownIcon !== undefined ? (
          typeof dropdownIcon === 'function' ? (
            dropdownIcon(isOpen)
          ) : (
            dropdownIcon
          )
        ) : (
          <svg
            className={cn(
              'strata:w-4 strata:h-4 strata:ml-2 strata:transition-transform strata:flex-shrink-0',
              isOpen && 'strata:rotate-180',
            )}
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
            aria-hidden='true'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M19 9l-7 7-7-7'
            />
          </svg>
        )}
      </button>

      {renderDropdown()}
    </div>
  )
}
